import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailTemplateService } from '@services';
import { useDebounce } from '@hooks/useDebounce';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import TemplatesSidebar from './TemplatesSidebar';
import EditorPanel from './EditorPanel';
import PreviewPanel from './PreviewPanel';
import PlaceholderReference from './PlaceholderReference';
import { showToast } from '@utils/toast';
import { getApiErrorMessage } from '@utils/apiError';
import { validateSubjectPlaceholders } from '@utils/subjectPlaceholders';
import type { ApiError } from '@app-types';
import styles from './EmailTemplatesTab.module.css';

/** Resolve the subject to show in the editor: the saved override, else the default. */
const resolveSubject = (template: { subject: string | null; default_subject: string | null }): string =>
  template.subject ?? template.default_subject ?? '';

const EmailTemplatesTab: React.FC = () => {
  const queryClient = useQueryClient();

  // State
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [editedSubject, setEditedSubject] = useState<string>('');
  const [editedBlocks, setEditedBlocks] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // Template code the admin is trying to switch to while there are unsaved
  // changes — gates the discard-confirmation modal below.
  const [pendingSwitchCode, setPendingSwitchCode] = useState<string | null>(null);
  // Inline placeholder-validation error surfaced under the subject field. Set
  // reactively from proactive client-side validation (see subjectValidationError)
  // and, as a fallback, from a 400 returned by the backend on save.
  const [backendSubjectError, setBackendSubjectError] = useState<string | undefined>(undefined);
  // Tracks which template code is seeded in the editor — gates the preview query
  // so stale debounced values from a prior template are never sent with a new code. (#6)
  const [seededCode, setSeededCode] = useState<string | null>(null);

  // Combine blocks + subject into one debounced value so both fields fire a single
  // preview POST with a consistent payload instead of two independent 500ms timers
  // that can produce a mixed-data request. (#8)
  const previewInput = useMemo(
    () => ({ blocks: editedBlocks, subject: editedSubject }),
    [editedBlocks, editedSubject]
  );
  const debouncedPreviewInput = useDebounce(previewInput, 500);

  // Fetch all templates
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => emailTemplateService.getEmailTemplates()
  });

  // Fetch selected template details
  const { data: selectedTemplate, isLoading: templateLoading } = useQuery({
    queryKey: ['email-template', selectedCode],
    queryFn: () => emailTemplateService.getEmailTemplate(selectedCode!),
    enabled: !!selectedCode
  });

  // Proactive client-side placeholder validation of the edited subject. Mirrors
  // the backend check so a malformed / unknown placeholder is caught before the
  // save round-trip; drives the inline subject error and disables Save. (#1)
  const subjectValidationError = useMemo(
    () => validateSubjectPlaceholders(editedSubject, selectedTemplate?.default_subject),
    [editedSubject, selectedTemplate?.default_subject]
  );

  // Inline error under the subject: proactive validation wins, else the backend 400.
  const subjectError = subjectValidationError ?? backendSubjectError;

  // Fetch preview. Gated on seededCode === selectedCode so stale debounced
  // values from the previous template never POST with the new code. (#6, #7)
  const {
    data: preview,
    isLoading: previewLoading,
    isError: previewIsError
  } = useQuery({
    queryKey: ['email-template-preview', selectedCode, debouncedPreviewInput],
    queryFn: () =>
      emailTemplateService.previewEmailTemplate(
        selectedCode!,
        debouncedPreviewInput.blocks,
        debouncedPreviewInput.subject
      ),
    // Don't preview while the subject is empty/invalid — a subject is required,
    // and an empty subject would 400 on the backend. (#subject-required)
    enabled:
      !!selectedCode &&
      selectedCode === seededCode &&
      Object.keys(debouncedPreviewInput.blocks).length > 0 &&
      !subjectValidationError
  });

  // Update template mutation
  const updateMutation = useMutation({
    mutationFn: ({ code, textBlocks, subject }: { code: string; textBlocks: Record<string, string>; subject?: string }) =>
      emailTemplateService.updateEmailTemplate(code, textBlocks, subject),
    onSuccess: (data) => {
      showToast.success('Template saved successfully');
      setHasChanges(false);
      setBackendSubjectError(undefined);
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      // Populate the detail cache directly instead of invalidating — avoids an
      // extra round-trip and shrinks the window where a refetch re-seeds the form
      // over characters the admin typed right after clicking Save. (#3, #15)
      queryClient.setQueryData(['email-template', selectedCode], data);
    },
    onError: (error: ApiError, variables) => {
      const isValidationError = error?.response?.status === 400;
      const message = getApiErrorMessage(error, 'Failed to save template');
      // Only pin the inline error to Subject when subject was part of the payload;
      // a 400 on a text block must not show a misleading red under Subject. (#2)
      if (isValidationError && variables.subject !== undefined) {
        setBackendSubjectError(message);
      }
      showToast.error(message);
    }
  });

  // Reset template mutation
  const resetMutation = useMutation({
    mutationFn: (code: string) => emailTemplateService.resetEmailTemplate(code),
    onSuccess: (data) => {
      showToast.success('Template reset to default');
      setShowResetConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      // Populate the cache directly — the seeding effect will re-seed the form
      // from the returned data, eliminating the duplicate manual seeding. (#15)
      queryClient.setQueryData(['email-template', selectedCode], data);
    },
    onError: (error: ApiError) => {
      showToast.error(getApiErrorMessage(error, 'Failed to reset template'));
    }
  });

  // Select first template when templates load
  useEffect(() => {
    if (templates && templates.length > 0 && !selectedCode) {
      setSelectedCode(templates[0].template_code);
    }
  }, [templates, selectedCode]);

  // Initialize form when template loads
  useEffect(() => {
    if (selectedTemplate) {
      const currentValues: Record<string, string> = {};
      const savedBlocks = selectedTemplate.text_blocks || {};
      for (const field of selectedTemplate.text_blocks_schema) {
        currentValues[field.key] = savedBlocks[field.key] ?? field.default;
      }
      setEditedBlocks(currentValues);
      setEditedSubject(resolveSubject(selectedTemplate));
      setBackendSubjectError(undefined);
      setHasChanges(false);
      // Record the just-seeded code so the preview gate opens; debouncedPreviewInput
      // will catch up within 500 ms. (#6)
      setSeededCode(selectedTemplate.template_code);
    }
  }, [selectedTemplate]);

  // Handle template selection
  const handleSelectTemplate = useCallback(
    (code: string) => {
      if (hasChanges) {
        // Defer the switch: surface a confirmation modal instead of the native
        // window.confirm so the discard prompt matches the rest of the app.
        setPendingSwitchCode(code);
        return;
      }
      setSelectedCode(code);
    },
    [hasChanges]
  );

  // Confirm discarding unsaved changes and switch to the pending template.
  const handleConfirmSwitch = useCallback(() => {
    if (pendingSwitchCode) {
      setSelectedCode(pendingSwitchCode);
    }
    setPendingSwitchCode(null);
  }, [pendingSwitchCode]);

  // Handle field change
  const handleFieldChange = useCallback((key: string, value: string) => {
    setEditedBlocks((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
    // A 400 can come from any field — clear the stale backend Subject error so it
    // doesn't linger after the admin corrects a text block. (#9)
    setBackendSubjectError(undefined);
  }, []);

  // Handle subject change
  const handleSubjectChange = useCallback((value: string) => {
    setEditedSubject(value);
    setHasChanges(true);
    // Proactive validation re-runs from editedSubject; clear any stale backend 400.
    setBackendSubjectError(undefined);
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    if (!selectedCode || subjectValidationError) return;
    // Always send the current subject: the backend clears the override when the
    // subject equals the default and leaves it unchanged only when omitted. (#2)
    // A subject is required (empty is blocked by subjectValidationError above).
    updateMutation.mutate({ code: selectedCode, textBlocks: editedBlocks, subject: editedSubject });
  }, [selectedCode, editedBlocks, editedSubject, subjectValidationError, updateMutation]);

  // Handle reset
  const handleReset = useCallback(() => {
    if (!selectedCode) return;
    resetMutation.mutate(selectedCode);
  }, [selectedCode, resetMutation]);

  // Check if template was modified from default
  const isModified = useMemo(() => {
    if (!selectedTemplate) return false;
    return selectedTemplate.is_modified;
  }, [selectedTemplate]);

  if (templatesLoading) {
    return (
      <div className={styles.emailTemplatesPage}>
        <div className={styles.loadingState}>Loading templates...</div>
      </div>
    );
  }

  return (
    <div className={styles.emailTemplatesPage} data-test-id="email-templates-page">
      {/* Header intentionally has no title: the page is rendered inside the Admin tab
          shell, which already labels it, so an in-page <h1> duplicated that heading.
          The element is kept for its bottom spacing above the templates layout. */}
      <header className={styles.pageHeader} />

      <div className={styles.templatesLayout}>
        <TemplatesSidebar
          templates={templates || []}
          selectedCode={selectedCode}
          hasChanges={hasChanges}
          onSelectTemplate={handleSelectTemplate}
        />

        <div className={styles.editorColumn}>
          {/* Per-template placeholder reference, contextually above the editor. */}
          <PlaceholderReference template={selectedTemplate} />

          <EditorPanel
            selectedTemplate={selectedTemplate}
            editedSubject={editedSubject}
            editedBlocks={editedBlocks}
            isModified={isModified}
            hasChanges={hasChanges}
            isSaving={updateMutation.isPending}
            isResetting={resetMutation.isPending}
            subjectError={subjectError}
            canSave={!subjectValidationError}
            onSubjectChange={handleSubjectChange}
            onFieldChange={handleFieldChange}
            onSave={handleSave}
            onResetClick={() => setShowResetConfirm(true)}
          />
        </div>

        <PreviewPanel
          previewHtml={preview?.html}
          previewSubject={preview?.subject}
          isLoading={previewLoading}
          templateLoading={templateLoading}
          isError={previewIsError}
        />
      </div>

      {/* Reset Confirmation Modal */}
      <ConfirmationModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleReset}
        title="Reset Template to Default?"
        message="This will restore all text fields to their original values. This action cannot be undone."
        confirmLabel="Reset to Default"
        confirmVariant="danger"
        isProcessing={resetMutation.isPending}
        dataTestId="reset-confirmation-modal"
      />

      {/* Discard-changes confirmation when switching templates */}
      <ConfirmationModal
        isOpen={pendingSwitchCode !== null}
        onClose={() => setPendingSwitchCode(null)}
        onConfirm={handleConfirmSwitch}
        title="Discard unsaved changes?"
        message="You have unsaved changes. Switching templates will discard them. This action cannot be undone."
        confirmLabel="Discard & Switch"
        confirmVariant="danger"
        dataTestId="switch-template-confirmation-modal"
      />
    </div>
  );
};

export default EmailTemplatesTab;
